/**
 * PATH: packages/email-hub/src/feature/components/WhoCanSeeButton.tsx
 * FILE_ID: EMAIL-WHOCANSEE-BUTTON-001
 * PURPOSE: Always-visible reader-header affordance showing WHO can see this
 *          email — a primary indicator (mailbox owner + count) that expands into
 *          a provenance flow chart (WhoCanSeeFlow) grouped by access channel.
 * WHY:     Operator (2026-06-03): "Each email should have a 'Who can see' with
 *          primary and then an expansion to see everyone who can see as a flow
 *          chart in the org structure." Email visibility is a real per-email set
 *          (owner / shared mailbox / board allocation / explicit / admins); this
 *          makes it visible + auditable from the reader.
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────────────┐   ┌──────────────────────┐
 *   │ click eye     │ → │ GET <id>/who-can-see/    │ → │ render WhoCanSeeFlow  │
 *   └──────────────┘   └──────────────────────────┘   └──────────────────────┘
 * DEPENDENCIES:
 *   - ../lib/api (emailApi): GET /api/emails/<id>/who-can-see/
 *   - ./WhoCanSeeFlow: the pure provenance flow chart
 *   - ../../_internal/components/Toast: error feedback
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Eye, Loader2 } from 'lucide-react'
import { emailApi } from '../lib/api'
import { cn } from '../../_internal/lib/utils'
import { useToast } from '../../_internal/components/Toast'
import { WhoCanSeeFlow } from './WhoCanSeeFlow'
import { type WhoCanSeeData } from './whoCanSee'

export function WhoCanSeeButton({
  emailId,
  open: openProp,
  onOpenChange,
}: {
  emailId: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const { toast } = useToast()
  const [internalOpen, setInternalOpen] = useState(false)
  const controlled = openProp !== undefined
  const open = controlled ? openProp : internalOpen
  const setOpen = useCallback(
    (next: boolean) => {
      if (controlled) onOpenChange?.(next)
      else setInternalOpen(next)
    },
    [controlled, onOpenChange],
  )
  const [data, setData] = useState<WhoCanSeeData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  // Reset cache when the email changes so a stale list never shows.
  const lastEmailId = useRef<string>(emailId)
  if (lastEmailId.current !== emailId) {
    lastEmailId.current = emailId
    if (data) setData(null)
  }

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, setOpen])

  const load = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await emailApi<WhoCanSeeData>(`/api/emails/${emailId}/who-can-see/`)
      setData(res)
    } catch {
      setError(true)
      toast('Could not load who can see this email.', 'error')
    } finally {
      setLoading(false)
    }
  }, [emailId, toast])

  const toggle = useCallback(() => {
    setOpen(!open)
    if (data === null && !loading) void load()
  }, [data, loading, load, open, setOpen])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Who can see this email"
        aria-label="Who can see this email"
        className={cn('p-1.5 rounded-md hover:bg-accent text-muted-foreground', open && 'bg-accent')}
        data-testid="email-who-can-see-button"
      >
        <Eye className="h-4 w-4" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Who can see this email"
          className="absolute top-full right-0 z-50 mt-1 w-80 max-h-[28rem] overflow-y-auto rounded-lg border border-border bg-popover p-2 shadow-lg"
          data-testid="email-who-can-see-popover"
        >
          <div className="px-1 pb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Eye className="h-3 w-3" aria-hidden /> Who can see this email
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 px-3 py-4 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              <span className="text-[11px]">Resolving visibility…</span>
            </div>
          ) : error ? (
            <div className="px-3 py-3 text-[11px] text-muted-foreground">
              Could not load visibility.{' '}
              <button
                type="button"
                onClick={() => void load()}
                className="font-medium text-blue-600 hover:underline dark:text-blue-400"
              >
                Retry
              </button>
            </div>
          ) : data && data.channels.length > 0 ? (
            <WhoCanSeeFlow data={data} />
          ) : (
            <div className="px-3 py-3 text-[11px] text-muted-foreground">
              No one but you can see this email yet.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default WhoCanSeeButton
